import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// components
import { ReduxSelect } from 'components/FormElements/ReduxForm'
// constants
import { Forms } from 'constants/forms'
// types
import { IOption } from 'components/FormElements/Select'
import { IField, IInjuryMasterIncidentProperty } from 'types/graphql'
// views
import { ValueMapTitle, ValueMapWrapper } from './views'

interface Props {
  name: string
  property: IInjuryMasterIncidentProperty
  sourceField: IField
}

const selector = formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)

// Which Injury Master option each option of the Skefto pick list stands for. An option left empty is
// matched by its label or value when Injury Master has an option with that name, and is otherwise an error.
const ValueMap: React.FC<Props> = ({ name, property, sourceField }) => {
  const dispatch = useDispatch()

  const valueMap: Record<string, IOption> | undefined = useSelector((state) => selector(state, `${name}.valueMap`))

  const choices = React.useMemo(() => property.choices ?? [], [property])

  const choiceOptions: IOption[] = React.useMemo(
    () => choices.map(({ value, label }) => ({ value: String(value), label })),
    [choices]
  )

  // Saved mappings only hold the code — show the Injury Master option's name once it is known.
  React.useEffect(() => {
    Object.entries(valueMap ?? {}).forEach(([from, option]) => {
      const match = option && option.label === option.value && choiceOptions.find((item) => item.value === option.value)

      if (match) {
        dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${name}.valueMap.${from}`, match))
      }
    })
  }, [choiceOptions, dispatch, name, valueMap])

  const options = sourceField.options ?? []

  if (!options.length) {
    return null
  }

  return (
    <ValueMapWrapper>
      <ValueMapTitle>Match each option to an Injury Master option</ValueMapTitle>
      {options.map((option) => {
        // The backend matches on the option's label or its stored value.
        const names = [option.label, option.value].filter(Boolean).map((name) => name.trim().toLowerCase())
        const sameName = choices.find((choice) => names.includes(choice.label.toLowerCase()))
        // Only an empty option falls back to the name match — a chosen one is sent as chosen
        const isChosen = valueMap?.[option.id]?.value !== undefined && valueMap?.[option.id]?.value !== null

        return (
          <ReduxSelect
            key={option.id}
            isClearable
            menuPortalTarget={null}
            label={option.label}
            options={choiceOptions}
            name={`${name}.valueMap.${option.id}`}
            helperText={!isChosen && sameName ? `Matched by name: ${sameName.label}` : undefined}
          />
        )
      })}
    </ValueMapWrapper>
  )
}

export default ValueMap
